/**
 * @file `POST /families/{familyId}/study/speak/{personId}` のクライアント。
 *
 * @what: 課題の場面と段ごとの基準と本人の音声を送り、書き起こし・届いた段・直すところ・相手役の
 *   次の一言を受け取る。
 * @why: 音声はサーバで評価に使ったあと捨てられ、どこにも保存されない。呼び出しは東京のリージョンに
 *   固定してある (設計 plan/docs/study_skill_task_design.md §5.2)。
 * @failure: AI が呼べないときは、使えない旨の応答が返る。呼び手は1行で知らせて再試行に委ねる。
 */

import { apiFetch } from "@/shared/api/client";
import type { StudyTextbookRouteTask } from "./textbookRoutes";

export interface StudySpeakCriterionInput {
  mark: number;
  /** 目盛りの名前を渡す。AI の答えの「届いた段」の名前に使う。 */
  label: string;
  must: string;
}

export interface StudySpeakInput {
  task_label: string;
  scene: string;
  role: string;
  opening?: string;
  criteria: StudySpeakCriterionInput[];
  /** 音声を符号化した文字列を渡す。 */
  audio: string;
  mime: string;
}

/** 相手の用件を取れていたかの値である。判断できなかったときは欠ける。 */
export type StudySpeakUnderstanding = "took" | "asked" | "missed";

export interface StudySpeakResult {
  transcript: string;
  /** 届いた段の目盛りの値が入る。どの段にも届かなければ0になる。 */
  reached_mark: number;
  reached_label?: string;
  reason?: string;
  fixes: string[];
  next_line?: string;
  clarity?: number;
  /** 聞き取りの結果が入る。届いた段と分けて返る。 */
  understanding?: StudySpeakUnderstanding;
  understanding_note?: string;
  model: string;
  prompt_version: string;
}

/** 聞き取りの結果を、画面に出す言葉にする。 */
export function understandingLabel(u: StudySpeakUnderstanding): string {
  switch (u) {
    case "took":
      return "用件を取れていた";
    case "asked":
      return "聞き返して確かめた";
    case "missed":
      return "用件を取り違えた";
  }
}

/**
 * 2つの結果から、次にどちらを練習するかを1行にする。
 *
 * @what: 聞き取りでつまずいていれば聞くこと、取れていて段が2以下なら返すことを指す。
 * @why: 届いた段だけでは、聞くことと返すことのどちらで詰まったかが分からない
 *   (設計 plan/docs/study_skill_task_design.md §5.3)。科目の名前は出さない。
 *   ※なぜならカリキュラムごとに科目の名前が違い、評価の側が特定の語を持つと他のカリキュラムで
 *   意味を失うためである。
 */
export function speakNextFocus(result: StudySpeakResult): string | null {
  if (result.understanding === "missed") return "聞き取りから見直すと、返しが楽になります。";
  if (result.understanding === undefined) return null;
  if (result.reached_mark <= 2) return "聞き取りは足りています。返す練習に重心を置きましょう。";
  return null;
}

export function evaluateSpeak(
  familyId: string,
  personId: string,
  input: StudySpeakInput,
): Promise<StudySpeakResult> {
  return apiFetch<StudySpeakResult>(
    `/families/${encodeURIComponent(familyId)}/study/speak/${encodeURIComponent(personId)}`,
    { method: "POST", body: input },
  );
}

/**
 * 課題と目盛りの名前から、送る材料を組み立てる。
 *
 * @what: 課題の場面と役と基準を引き継ぎ、基準に目盛りの名前を付ける。相手役の一言は、やりとりの
 *   途中なら前の返しを使う。
 * @why: 目盛りの名前はルートの側にあり、課題は値しか持たない。AI が「届いた段」を名前で言えるように
 *   ここで合わせる。
 */
export function speakInputFrom(
  task: StudyTextbookRouteTask,
  markLabels: ReadonlyMap<number, string>,
  audioBase64: string,
  mime: string,
  opening: string,
): StudySpeakInput {
  const input: StudySpeakInput = {
    task_label: task.label,
    scene: task.scene,
    role: task.role,
    criteria: task.criteria.map((c) => ({
      mark: c.mark,
      label: markLabels.get(c.mark) ?? String(c.mark),
      must: c.must,
    })),
    audio: audioBase64,
    mime,
  };
  if (opening) input.opening = opening;
  return input;
}
